import React, { useState, useRef, useEffect, memo } from 'react';
import apiRequest from "../../services/apiClient";
import { formatDate } from "../../utils/utils";

// Singleton para manejar una sola instancia de ClientDetail
class ClientDetailManager {
  constructor() {
    this.currentRequest = null;
    this.isLoading = false;
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify(data) {
    this.listeners.forEach(listener => listener(data));
  }

  async loadClient(selectedItem) {
    if (this.isLoading) {
      console.log('Already loading, ignoring request for:', selectedItem);
      return;
    }

    console.log('Making API request for client:', selectedItem);
    this.isLoading = true;
    
    // Cancelar petición anterior si existe
    if (this.currentRequest) {
      console.log('Cancelling previous request');
      this.currentRequest.cancel?.();
    }
    
    const request = apiRequest(`client/${selectedItem}?$expand=userCreator($select=fullName),userModifier($select=fullName)`);
    this.currentRequest = request;
    
    try {
      const response = await request;
      console.log('API response received for client:', selectedItem, response.data);
      this.notify({ type: 'SUCCESS', data: response.data });
    } catch (error) {
      console.error('Error loading client:', error);
      this.notify({ type: 'ERROR', error });
    } finally {
      this.isLoading = false;
      this.currentRequest = null;
    }
  }

  clear() {
    this.isLoading = false;
    if (this.currentRequest) {
      this.currentRequest.cancel?.();
      this.currentRequest = null;
    }
    this.notify({ type: 'CLEAR' });
  }
}

const clientDetailManager = new ClientDetailManager();

const ClientDetailSingleton = memo(({ selectedItem, handleUnselectItem, handleEditItem, handleDeleteItem }) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = clientDetailManager.subscribe(({ type, data, error }) => {
      switch (type) {
        case 'SUCCESS':
          setClient(data);
          setLoading(false);
          break;
        case 'ERROR':
          setLoading(false);
          break;
        case 'CLEAR':
          setClient(null);
          setLoading(false);
          break;
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (selectedItem) {
      setLoading(true);
      clientDetailManager.loadClient(selectedItem);
    } else {
      clientDetailManager.clear();
    }
  }, [selectedItem]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <p className="text-gray-500">No client selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={client.name || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identity Number</label>
            <input
              type="text"
              value={client.identityNumber || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
            <input
              type="text"
              value={formatDate(client.created) || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          {client.updated && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Updated</label>
              <input
                type="text"
                value={formatDate(client.updated)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          {client.userCreator && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Creator</label>
              <input
                type="text"
                value={client.userCreator?.fullName || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          {client.userModifier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Modifier</label>
              <input
                type="text"
                value={client.userModifier?.fullName || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identifier</label>
            <input
              type="text"
              value={client.id || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>
        </div>
      </div>

      {/* Fixed Actions */}
      <div className="p-6 pt-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <button
            onClick={handleUnselectItem}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleEditItem}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteItem}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
});

ClientDetailSingleton.displayName = 'ClientDetailSingleton';

export default ClientDetailSingleton;
